import { Lock, Trash2, UserPlus } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import {
  INTERVIEW_MODE_LABELS,
  INTERVIEW_MODES,
  MAX_PANEL,
  PANEL_WEIGHT_MAX,
  PANEL_WEIGHT_MIN,
  equalShares,
  panelWeightProblems,
  roundNameAt,
  type AddGuestPanelistInput,
  type InterviewMode,
  type InterviewPlanOptions,
  type KitOption,
  type PanelCandidate,
  type PanelMember,
  type TemplateRound,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { ModuleGate } from '../../../components/ModuleGate';
import { Popover, usePopover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { Select } from '../../../components/Select';
import { wholeNumber } from '../../../lib/whole-number';
import styles from './RoundEditor.module.css';

/**
 * One interview round — the Interview step's round setup (C5, ADR 0077) and the plan template editor
 * (ADMIN-03), so a template and a job can never be set up differently. What a round asks for now: how
 * it's held, and a panel of up to five with each person's share of the round result (equal unless HR
 * changes them; 10%–80% each, 100% in all). Its name is its place ("Round 1"), the length is an hour
 * unless one booking changes it, and the old rule, pass mark and blind switch are gone from the screen
 * (their saved values stay; the round is always blind, server-side).
 */

interface RoundEditorProps {
  readonly round: TemplateRound;
  readonly index: number;
  readonly options: InterviewPlanOptions | undefined;
  readonly isReadOnly: boolean;
  /** A candidate has reached this round: editing makes a new config version. */
  readonly isLocked?: boolean;
  /** Server messages for this round, already stripped of their `rounds.n.` prefix. */
  readonly errors?: Readonly<Record<string, string>>;
  readonly onChange: (next: TemplateRound) => void;
  /** Absent when this round cannot be removed. */
  readonly onRemove?: () => void;
  /** C6-a: adds someone with no account by name and email; absent where there is no job (a plan template). */
  readonly onAddGuest?: (input: AddGuestPanelistInput) => Promise<PanelCandidate>;
  /** Wraps the editor in its own list item (the plan template editor's list); off for a single round on a page. */
  readonly isListItem?: boolean;
  /**
   * Admin redesign batch 2 (Q2 a): the round picks an interview kit — a job's round only, never a plan's (a kit is chosen on
   * the job). Shown only while Interview kits is on (#13).
   */
  readonly hasKitPicker?: boolean;
}

export function RoundEditor({
  round,
  index,
  options,
  isReadOnly,
  isLocked = false,
  errors = {},
  onChange,
  onRemove,
  onAddGuest,
  isListItem = true,
  hasKitPicker = false,
}: RoundEditorProps): ReactNode {
  const change = (patch: Partial<TemplateRound>): void => onChange({ ...round, ...patch });
  // The kit's own problem shows under its picker, not again in the round's list.
  const messages = [...new Set(Object.entries(errors).flatMap(([field, message]) => (field === 'interviewKitId' && hasKitPicker ? [] : [message])))];
  const Wrapper = isListItem ? 'li' : 'div';

  return (
    // On a page (the Interview step) the round sits inside its own card, which names it — no second frame or title.
    <Wrapper className={isListItem ? styles.round : styles.roundBare}>
      <div className={styles.head}>
        {isListItem ? <h3 className={styles.number}>{roundNameAt(index)}</h3> : <span className={styles.spacer} />}
        {isLocked && (
          <span className={styles.locked}>
            <Lock size={12} aria-hidden /> Candidates have reached this round
          </span>
        )}
        {onRemove && !isReadOnly && (
          <Button size="sm" variant="ghost" icon={Trash2} onClick={onRemove} aria-label={`Remove ${roundNameAt(index)}`}>
            Remove
          </Button>
        )}
      </div>

      <Field label="How it's held" hint="Each interview is booked for an hour; a booking can change its own.">
        {({ describedBy }) => (
          <div aria-describedby={describedBy}>
            <Segmented
              label="How the round is held"
              disabled={isReadOnly}
              value={round.mode}
              options={INTERVIEW_MODES.map((mode) => ({ value: mode, label: INTERVIEW_MODE_LABELS[mode] }))}
              onChange={(mode: InterviewMode) => change({ mode })}
            />
          </div>
        )}
      </Field>

      <PanelEditor round={round} options={options} isReadOnly={isReadOnly} onChange={(panel) => change({ panel })} onAddGuest={onAddGuest} />

      {hasKitPicker && (
        <ModuleGate module="interview_kits">
          <KitPicker kitId={round.interviewKitId} kits={options?.kits ?? []} isReadOnly={isReadOnly} error={errors.interviewKitId} onChange={(interviewKitId) => change({ interviewKitId })} />
        </ModuleGate>
      )}

      {messages.length > 0 && (
        <ul className={styles.errors} role="alert">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
    </Wrapper>
  );
}

const NO_KIT = 'none';

/**
 * Q2 a: the round's interview kit. Picking one pins that exact version, so editing the kit later never changes this job
 * (non-negotiable #4); the round's interviews not yet held take it too. A round on an older version keeps it, named as such,
 * with the newer one a click away. Nothing shows while there is no kit to pick.
 */
function KitPicker({
  kitId,
  kits,
  isReadOnly,
  error,
  onChange,
}: {
  readonly kitId: string | null;
  readonly kits: readonly KitOption[];
  readonly isReadOnly: boolean;
  readonly error: string | undefined;
  readonly onChange: (kitId: string | null) => void;
}): ReactNode {
  const pinned = kits.find((kit) => kit.id === kitId) ?? null;
  const newer = pinned && !pinned.isCurrent ? (kits.find((kit) => kit.familyId === pinned.familyId && kit.isCurrent) ?? null) : null;
  if (kits.length === 0 && kitId === null) return null;
  const describe = (kit: KitOption): string => [`${kit.questionCount} question${kit.questionCount === 1 ? '' : 's'}`, `version ${kit.versionNumber}`, kit.description].filter(Boolean).join(' · ');
  const options = [
    { value: NO_KIT, label: 'No kit', description: 'Interviewers get no suggested questions' },
    ...kits.filter((kit) => kit.isCurrent || kit.id === kitId).map((kit) => ({ value: kit.id, label: kit.isCurrent ? kit.name : `${kit.name} (older version ${kit.versionNumber})`, description: describe(kit) })),
  ];
  return (
    <Field label="Interview kit" isOptional hint="Interviewers see its questions during the interview. The round keeps this exact version, even if the kit is edited later." {...(error ? { error } : {})}>
      {({ inputId, describedBy, isInvalid }) => (
        <div className={styles.kitPick}>
          <Select
            id={inputId}
            describedBy={describedBy}
            isInvalid={isInvalid}
            label="Interview kit"
            placeholder="No kit"
            disabled={isReadOnly}
            value={kitId ?? NO_KIT}
            options={options}
            onChange={(value) => onChange(value === NO_KIT ? null : value)}
          />
          {newer && !isReadOnly && (
            <span className={styles.why}>
              Version {newer.versionNumber} is newer.{' '}
              <Button size="sm" variant="ghost" onClick={() => onChange(newer.id)}>
                Use version {newer.versionNumber}
              </Button>
            </span>
          )}
        </div>
      )}
    </Field>
  );
}

/** C5: whether a panel's shares were set by hand (every counting seat has one). */
function isWeighted(panel: readonly PanelMember[]): boolean {
  const counting = panel.filter((member) => member.role !== 'observer');
  return counting.length > 1 && counting.every((member) => member.weightPercent !== null);
}

/** Every counting seat's share as HR sees it: the saved one, else the equal split in whole percent. */
function sharesOf(panel: readonly PanelMember[]): ReadonlyMap<string, number> {
  const counting = panel.filter((member) => member.role !== 'observer');
  if (isWeighted(panel)) return new Map(counting.map((member) => [member.userId, member.weightPercent ?? 0]));
  const equal = equalShares(counting.length);
  return new Map(counting.map((member, position) => [member.userId, equal[position] ?? 0]));
}

/** Who sits on the round (F5.10), with each person's share of the round result (C5). */
function PanelEditor({
  round,
  options,
  isReadOnly,
  onChange,
  onAddGuest,
}: {
  readonly round: TemplateRound;
  readonly options: InterviewPlanOptions | undefined;
  readonly isReadOnly: boolean;
  readonly onChange: (panel: TemplateRound['panel']) => void;
  readonly onAddGuest: ((input: AddGuestPanelistInput) => Promise<PanelCandidate>) | undefined;
}): ReactNode {
  const people = options?.people ?? [];
  const personOf = (userId: string): PanelCandidate | undefined => people.find((person) => person.id === userId);
  const nameOf = (userId: string): string => personOf(userId)?.fullName ?? 'Someone no longer active';
  // BH-24 / ADR 0071: only people who may interview are offered; someone already seated who no longer qualifies stays by name.
  const available = people.filter((person) => person.canInterview !== false && !round.panel.some((member) => member.userId === person.id));
  const isFull = round.panel.length >= MAX_PANEL;
  const shares = sharesOf(round.panel);
  const counting = round.panel.filter((member) => member.role !== 'observer');
  const isSharesShown = counting.length > 1;
  const problems = panelWeightProblems(round.panel);

  /** A new seat or one taken off: back to equal shares, which always stand. */
  const seat = (panel: readonly PanelMember[]): void => onChange(panel.map((member) => ({ ...member, weightPercent: null })));

  function setShare(userId: string, percent: number): void {
    onChange(round.panel.map((member) => (member.role === 'observer' ? member : { ...member, weightPercent: member.userId === userId ? percent : (shares.get(member.userId) ?? null) })));
  }

  return (
    <div className={styles.panel}>
      <span className={styles.panelTitle}>Panel</span>
      {round.panel.length === 0 && <p className={styles.why}>Nobody yet. Add up to {MAX_PANEL} people who interview in this round.</p>}
      <ul className={styles.members}>
        {round.panel.map((member) => {
          const person = personOf(member.userId);
          return (
            <li key={member.userId} className={styles.member}>
              <span className={styles.memberWho}>
                <span className={styles.memberName}>{nameOf(member.userId)}</span>
                {person?.isGuest && <Chip tone="neutral">No account · scores by link</Chip>}
                {person?.canInterview === false && <Chip tone="attention">Can't interview here</Chip>}
                {member.role === 'observer' && <Chip tone="neutral">Observer</Chip>}
              </span>
              {isSharesShown && member.role !== 'observer' ? (
                <span className={styles.share}>
                  <Input
                    isNumeric
                    inputMode="numeric"
                    suffix="%"
                    aria-label={`${nameOf(member.userId)}'s share of the round`}
                    disabled={isReadOnly}
                    value={String(shares.get(member.userId) ?? 0)}
                    onChange={(event) => setShare(member.userId, wholeNumber(event.target.value))}
                  />
                </span>
              ) : (
                <span className={styles.shareFixed}>{member.role === 'observer' ? '—' : '100%'}</span>
              )}
              {!isReadOnly && (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Trash2}
                  aria-label={`Take ${nameOf(member.userId)} off the panel`}
                  onClick={() => seat(round.panel.filter((other) => other.userId !== member.userId))}
                />
              )}
            </li>
          );
        })}
      </ul>
      {isSharesShown && (
        <div className={styles.shareFoot}>
          <span className={styles.why}>
            {isWeighted(round.panel) ? `Shares set by hand: each ${PANEL_WEIGHT_MIN}%–${PANEL_WEIGHT_MAX}%, ${[...shares.values()].reduce((sum, share) => sum + share, 0)}% in all.` : 'Equal shares. Change one to weigh someone more.'}
          </span>
          {isWeighted(round.panel) && !isReadOnly && (
            <Button size="sm" variant="ghost" onClick={() => seat(round.panel)}>
              Make shares equal
            </Button>
          )}
        </div>
      )}
      {problems.length > 0 && (
        <ul className={styles.errors} role="alert">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}
      {!isReadOnly && !isFull && (
        <div className={styles.addRow}>
          {available.length > 0 && (
            <div className={styles.addPerson}>
              <UserPlus size={14} aria-hidden className={styles.addIcon} />
              <Select
                label="Add someone to the panel"
                placeholder="Add someone…"
                value={null}
                options={available.map((person) => ({ value: person.id, label: person.fullName, description: person.isGuest ? `${person.email} · no account` : person.email }))}
                onChange={(userId) => seat([...round.panel, { userId, role: 'counting', weightPercent: null }])}
              />
            </div>
          )}
          {onAddGuest && <AddGuest onAdd={onAddGuest} onAdded={(person) => seat([...round.panel, { userId: person.id, role: 'counting', weightPercent: null }])} />}
        </div>
      )}
      {isFull && !isReadOnly && <p className={styles.why}>A panel holds {MAX_PANEL} people at most.</p>}
    </div>
  );
}

/** C6-a: someone with no account, by name and email, in a pop-up anchored to its button (CLAUDE.md §5). */
function AddGuest({ onAdd, onAdded }: { readonly onAdd: (input: AddGuestPanelistInput) => Promise<PanelCandidate>; readonly onAdded: (person: PanelCandidate) => void }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setIsAdding(true);
    setProblem(null);
    try {
      const person = await onAdd({ fullName: fullName.trim(), email: email.trim() });
      onAdded(person);
      setFullName('');
      setEmail('');
      close();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'They could not be added. Try again.');
    } finally {
      setIsAdding(false);
    }
  }

  return (
    <>
      <Button ref={triggerRef} size="sm" variant="ghost" icon={UserPlus} onClick={toggle}>
        Add someone without an account
      </Button>
      <Popover anchor={anchor} onClose={close} label="Add someone without an account">
        <form className={styles.guestForm} onSubmit={(event) => void submit(event)}>
          <p className={styles.why}>They get their own link by email to give their mark. No account or password needed.</p>
          <Field label="Name" isRequired>
            {({ inputId }) => <Input id={inputId} value={fullName} autoComplete="name" onChange={(event) => setFullName(event.target.value)} />}
          </Field>
          <Field label="Email" isRequired {...(problem ? { error: problem } : {})}>
            {({ inputId }) => <Input id={inputId} type="email" inputMode="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} />}
          </Field>
          <Button type="submit" variant="solid" disabled={isAdding || fullName.trim().length < 2 || !email.includes('@')}>
            Add to the panel
          </Button>
        </form>
      </Popover>
    </>
  );
}

/** The server's `rounds.1.panel.0.userId` messages, for round 1, without the prefix. */
export function roundErrors(errors: Readonly<Record<string, string>>, index: number): Readonly<Record<string, string>> {
  const prefix = `rounds.${index}.`;
  return Object.fromEntries(
    Object.entries(errors)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, message]) => [key.slice(prefix.length), message]),
  );
}
